import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { IVideoApi } from '../api/interfaces/video-abstract.api';
import { VideoApi } from '../api/video.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { AbstractFacade } from './abstract.facade';
import { ApiHttpResponse, VideoSection } from '@kinomap-dev/ng-ui-lib';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'app/services/toaster.service';

// 3 minutes
const CACHE_VALIDITY: number = 180000;

export interface VideosFlowState {
  loading: boolean;
  videos: VideoSection[];
  total: number | null;
  lastRequestedIndex: number;
}

@Injectable({
  providedIn: 'root',
})
export class VideosFacade extends AbstractFacade {
  private cacheValidityTime: number = 0;
  private state: VideosFlowState = {
    loading: false,
    videos: [],
    total: null,
    lastRequestedIndex: 0,
  };
  public readonly store: BehaviorSubject<VideosFlowState> =
    new BehaviorSubject<VideosFlowState>(this.state);
  public readonly state$: Observable<VideosFlowState> =
    this.store.asObservable();

  public readonly videos$: Observable<VideoSection[]> = this.state$.pipe(
    map((state: VideosFlowState) => state.videos),
    distinctUntilChanged()
  );
  public readonly total$: Observable<number> = this.state$.pipe(
    map((state: VideosFlowState) => state.total),
    distinctUntilChanged()
  );
  public readonly loading$: Observable<boolean> = this.state$.pipe(
    map((state: VideosFlowState) => state.loading),
    distinctUntilChanged()
  );

  constructor(
    private readonly videoApi: IVideoApi,
    @Inject(NEVER_TOKEN) @Optional() public userId: number,
    private toasterService: ToasterService,
    private translate: TranslateService
  ) {
    super();
  }

  getState$(): Observable<VideosFlowState> {
    return this.state$;
  }

  private setState(newState: Partial<VideosFlowState>) {
    this.state = { ...this.state, ...newState };
    this.store.next(this.state);
  }

  reset(): void {
    this.setState({
      loading: false,
      videos: [],
      total: null,
      lastRequestedIndex: 0,
    });
    // TODO -> Unsubcribe get subscriptions
  }

  loadFromStart(count: number) {
    const end: number = count - 1;
    if (this.isCacheValid()) {
      // Check if there is still something to load
      if (end > this.state.lastRequestedIndex + 1) {
        this.loadFromApi(this.state.lastRequestedIndex + 1, end);
      }
    } else {
      this.reset();
      this.loadFromApi(0, end);
    }
  }

  load(start: number, count: number): void {
    if (start <= 0) {
      this.loadFromStart(count);
    } else {
      const end: number = start + count - 1;
      // Check if there is still something to load
      if (end > this.state.lastRequestedIndex + 1) {
        this.loadFromApi(this.state.lastRequestedIndex + 1, end);
      }
    }
  }

  loadFromApi(start: number, end: number) {
    // Do not reload if a page is currently loading
    if (end <= 0 || this.isTotallyLoaded() || this.state.loading) {
      return;
    }

    const { page, pageSize }: { page: number; pageSize: number } =
      this.computePagesAndSizeFromInterval(start, end);
    const offset: number = (page - 1) * pageSize;

    this.setState({
      loading: true,
      lastRequestedIndex: end,
    });

    this.videoApi.getVideos(page, pageSize, this.userId).subscribe({
      next: (videos: ApiHttpResponse<VideoSection[]>) => {
        const allVideos: VideoSection[] = this.mergeNewItemsToOldOnes(
          videos.data,
          offset
        );

        // TODO => A voir si il le faire seulement au début
        this.cacheValidityTime = Date.now() + CACHE_VALIDITY;
        this.setState({
          loading: false,
          videos: allVideos,
          total: videos.meta.total,
        });
        // TODO => Run following data in queue
      },
      error: (err) => {
        console.error(err);
        this.setState({
          loading: false,
        });
      },
    });
  }

  private computePagesAndSizeFromInterval(
    start: number,
    end: number
  ): { page: number; pageSize: number } {
    const numberWantedItems: number = end - start + 1;
    if (start <= 0 || start < numberWantedItems) {
      return {
        page: 1,
        pageSize: end + 1,
      };
    }

    for (let size = numberWantedItems; size <= start; ++size) {
      const pageBefore: number = Math.floor(start / size);
      const newStart: number = pageBefore * size;
      if (newStart + size > end) {
        return {
          page: pageBefore + 1,
          pageSize: size,
        };
      }
    }

    // Should never go here
    return {
      page: 1,
      pageSize: end + 1,
    };
  }

  /**
   * It places new activities at the right index in the array
   */
  private mergeNewItemsToOldOnes(newItems: VideoSection[], offset: number) {
    const mergedItems: VideoSection[] = [...this.state.videos];
    newItems.forEach(
      (video: VideoSection, index: number) =>
        (mergedItems[index + offset] = video)
    );
    return mergedItems;
  }

  isFirstPageLoaded(): boolean {
    return this.state.total !== null;
  }

  isTotallyLoaded(): boolean {
    return (
      this.state.total !== null && this.state.videos.length >= this.state.total
    );
  }

  isLoading(): boolean {
    return this.state.loading;
  }

  isCacheValid() {
    return this.cacheValidityTime > Date.now();
  }

  deleteVideo(videoId: number) {
    (this.videoApi as VideoApi).deleteVideo(videoId).subscribe({
      error: () => {
        const error: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-error'
        );
        this.toasterService.warning(null, error);
      },
      next: () => {
        const success: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-success'
        );
        this.toasterService.success(null, success);
        const videoIndex: number = this.state.videos.findIndex(
          (video: VideoSection) => video.id === videoId
        );

        if (videoIndex === -1) {
          return;
        }

        const newVideos: VideoSection[] = [...this.state.videos];
        newVideos.splice(videoIndex, 1);
        this.setState({
          videos: newVideos,
          total: this.state.total - 1,
          lastRequestedIndex: this.state.lastRequestedIndex - 1,
        });
      },
    });
  }
}
